import React, { FC, useState, useEffect, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Typography } from './Typography';
import { Panel } from './Panel';
import { CaretDown } from './icon/icons/CaretDown';
import { ThemeFontsKeys } from '../styles/tokens/typography';
import { useDataLayerPush } from '../hooks/useDataLayerPush';

export interface ExpanderPanelProps {
  title: string;
  titleLight?: string;
  children: any;
  as?: 'div' | 'section' | 'li';
  headingComponent?: any;
  headingVariant?: ThemeFontsKeys;
  open?: boolean;
  disabled?: boolean;
}

interface StyledExpanderPanelProps {
  isOpen: boolean;
}

interface StyledHeadingButtonProps {
  largePadding: boolean;
  disabled: boolean;
}

export const StyledExpanderHeading = styled(Typography)`
  ${({ theme: { global, borders } }) => css`
    border: ${borders.default};
    border-top-left-radius: ${global.borderRadius.default};
    border-top-right-radius: ${global.borderRadius.default};
  `};
`;

const StyledHeadingButton = styled.button<StyledHeadingButtonProps>`
  ${({ theme: { space, colors }, largePadding, disabled }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    background-color: ${colors.white.default};
    border: none;
    padding: ${largePadding ? `${space.md} ${space.lg}` : space.md};
    opacity: ${disabled ? '0.5' : '1'};
    cursor: ${disabled ? 'default' : 'pointer'};
    text-align: left;

    ${!disabled &&
    css`
      &:hover {
        color: ${colors.brand.default};
      }
    `};
  `};
`;

const StyledHeadingLight = styled.span`
  ${() => css`
    font-weight: 300;
  `};
`;

const StyledHeadingIcon = styled(CaretDown)`
  ${() => css`
    transition: all 0.25s ease-in-out;
  `};
`;

export const StyledExpanderContent = styled(Panel)`
  ${({ theme: { colors } }) => css`
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-top: none;
    background-color: ${colors.white.default};
  `};
`;

export const StyledExpanderPanel = styled.div<StyledExpanderPanelProps>`
  ${({ isOpen }) => css`
    /* Animate content opening */
    ${StyledExpanderContent} {
      display: ${isOpen ? 'block' : 'none'};
    }

    /* Animate Icon opening */
    ${StyledHeadingIcon} {
      transform: rotate(${isOpen ? '180deg' : '0'});
    }
  `};
`;

export const ExpanderPanel: FC<React.PropsWithChildren<ExpanderPanelProps>> = forwardRef(
  (
    {
      title,
      titleLight,
      children,
      as,
      headingVariant = 'titleSmall',
      headingComponent = 'h3',
      open,
      disabled,
      ...rest
    },
    ref
  ) => {
    const { dataLayerPush } = useDataLayerPush();
    const [isOpen, setIsOpen] = useState(open);
    const id = title.replace(/\s+/g, '-').toLowerCase();
    const buttonID = `expander-button-${id}`;
    const panelID = `expander-panel-${id}`;

    const handleClick = () => {
      if (disabled) {
        return;
      }

      dataLayerPush({
        event: 'click accordion',
        clickText: title,
        clickAccordionAction: isOpen ? 'closed' : 'opened',
      });

      setIsOpen(!isOpen);
    };

    useEffect(() => setIsOpen(open), [open]);

    return (
      <StyledExpanderPanel {...rest} as={as} isOpen={isOpen} ref={ref}>
        <StyledExpanderHeading variant={headingVariant} component={headingComponent}>
          <StyledHeadingButton
            type="button"
            onClick={() => {
              handleClick();
            }}
            id={buttonID}
            aria-controls={panelID}
            aria-expanded={isOpen}
            largePadding={headingVariant !== 'titleSmall'}
            disabled={disabled}
          >
            <span>
              {title} {titleLight && <StyledHeadingLight>{titleLight}</StyledHeadingLight>}
            </span>
            <StyledHeadingIcon />
          </StyledHeadingButton>
        </StyledExpanderHeading>
        <StyledExpanderContent
          id={panelID}
          tab-index={!isOpen && -1}
          aria-labelledby={buttonID}
          aria-hidden={!isOpen}
          border
        >
          {children}
        </StyledExpanderContent>
      </StyledExpanderPanel>
    );
  }
);
